/*
 * Copyright (c) 2023. Tooling Team
 */

import { RouteDisplay } from "../components/navigation/RouteDisplay";
import Layout from "../components/layout/Layout";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Layout as AbyssLayout } from "@uhg-abyss/web/ui/Layout";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import EIPAddEditRowForm from "../components/table/EIPAddEditRowForm";
import DeleteRowForm from "../components/table/DeleteRowForm";
import BulkUploadForm from "../components/table/BulkUploadForm";
import BulkUploadPreviewForm from "../components/table/BulkUploadPreviewForm";
import BulkUploadResultsForm from "../components/table/BulkUploadResultsForm";
import {
  LAB_DOMAIN_VALUE,
  PATHS,
  SELECT_SEARCH_CONCEPT_SETS_BY_CONDITION_LAB_DOMAIN_CONDITION,
} from "../utils/constants";
import useEIPMapValueDefinition from "../hooks/useEIPMapValueDefinition";
import useEIPSubmissionOperations from "../hooks/useEIPSubmissionOperations";
import SubmissionPopup from "../components/SubmissionPopup";
import {
  getRecordRowId,
  transformMVRecordsForApiRequest,
} from "../utils/funcs";
import {
  getBulkUploadFormData,
  handleValidatedRows,
  resolveBulkUploadFile,
} from "../components/table/BulkUploadUtils";
import MapLookupContainer from "../components/MapLookupContainer";
import EIPMapValueDefinitionContainer from "../components/EIPMapValueDefinitionContainer";
import {
  useBulkOperationMutation,
  useSearchMapsMutation,
  useUpdateMapTagsMutation,
  useUpdateMapMutation,
} from "../app/services/mapsApi";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFormData,
  setTableLoading,
  setSelectedMapId,
  setIsSearchDisabled,
  setAddFormData,
  setMultiEditFormData,
  setMultiDeleteFormData,
  setUpdatedRecords,
  setCloneRecords,
  setRowData,
  setMapVersionList,
  setMapStatus,
  resetFilters,
  setAttrFilters,
} from "../app/slices/EIPMapValueDefinition.slice";
import { resetNewMapLocation } from "../app/slices/EIPCreateMap.slice";
import {
  useGetAttrConfigQuery,
  useLazyGetAttributesQuery,
} from "../app/services/attributesApi";
import useClone from "../hooks/useClone";
import {
  useGetLabDomainClientsQuery,
  useLazyGetLabDomainCodeMappingQuery,
  useUpdateLabDomainCodeMappingMutation,
} from "../app/services/labDomainApi";
import {
  selectConceptSetsLoincValues,
  useSearchConceptSetsByConditionQuery,
} from "../app/services/conceptSetsApi";
import {
  clearLabMvdReturnState,
  readLabMvdReturnState,
} from "../components/table/LabDomainCodeMapping/utils/navigationState";

const DEFAULT_SELECTION_METADATA = {
  source: "none",
  isSelectAllChecked: false,
  hasActiveFilter: false,
  selectedSelectableCount: 0,
  totalSelectableCount: 0,
  useAllMapTarget: false,
};
const DEFAULT_SUBMISSION_LOADING_TITLE = "Loading...";

const UNCHANGED = "UNCHANGED";

//helper function for standard code dropdown
export const buildStandardCodeDropdownValues = (conceptValues, mode) => {
  let index = -1;
  const standardCodeDropdownValues = [];

  if (conceptValues) {
    conceptValues.forEach((concept) => {
      const code = concept.code ?? "";

      standardCodeDropdownValues.push({
        id: index++,
        label: code,
        value: code,
        description: concept.description ?? "",
      });
    });
  }

  standardCodeDropdownValues.sort((a, b) => a.value.localeCompare(b.value));

  if (mode === "Bulk Edit" || mode === "Clone") {
    standardCodeDropdownValues.unshift({
      id: -2, //an arbitrary "unchanged" negative number to avoid collision with other ids
      label: UNCHANGED,
      value: UNCHANGED,
    });
  }

  return standardCodeDropdownValues;
};

export const getPromotedLabDomainLocation = (state) => {
  const promotedMap = state?.promotedLabDomainMap;
  if (!promotedMap) return null;

  const attributes = promotedMap.attributes || {};
  return {
    state: {
      Client: attributes.Client,
      Domain: attributes.Domain,
      Standard_Code_System: attributes.Standard_Code_System,
    },
  };
};

export const getReturnLabMvdLocation = (returnState) => {
  const searchValues = returnState?.searchValues;
  if (!searchValues) return null;

  return {
    state: {
      Client: searchValues.Client,
      Domain: searchValues.Domain,
      Standard_Code_System: searchValues.Standard_Code_System,
    },
  };
};

const normalizeComparableValue = (value) =>
  value === undefined || value === null ? "" : String(value).toLowerCase();

const valuesMatch = (a, b) =>
  normalizeComparableValue(a) === normalizeComparableValue(b);

const rowMatchesTagFilter = (row, key, filters) =>
  row.tags &&
  row.tags.some((tag) =>
    tag.values.some((keyElement) => filters[key].includes(keyElement))
  );

export const buildLabMapVersionList = (version) => {
  const maxVersion = Number(version);
  if (!Number.isInteger(maxVersion) || maxVersion < 1) {
    return [];
  }

  return Array.from({ length: maxVersion }, (_, index) => maxVersion - index);
};

export const findLabClientMap = (labClients = [], searchValues = {}) =>
  labClients.find(
    (labClient) =>
      (valuesMatch(labClient?.client, searchValues.Client) ||
        valuesMatch(labClient?.name, searchValues.Client)) &&
      valuesMatch(labClient?.domain, searchValues.Domain) &&
      valuesMatch(
        labClient?.standardCodeSystem,
        searchValues.Standard_Code_System
      )
  );

const getMissingLabClientError = (values = {}) =>
  new Error(`No Lab Domain data found for ${values.Client}.`);

const EIPMapValueDefinition = () => {
  const location = useLocation();
  const initialLabMvdReturnStateRef = useRef(
    location.state?.labMvdReturnState || readLabMvdReturnState()
  );
  const promotedLabDomainLocation = getPromotedLabDomainLocation(
    location.state
  );
  const returnLabMvdLocation = getReturnLabMvdLocation(
    initialLabMvdReturnStateRef.current
  );
  const { selectedDomain, selectedSystem, client } = useSelector(
    (state) => state.createMap || {}
  );

  const [newMapLocation, setNewMapLocation] = useState({
    state: {
      Client:
        promotedLabDomainLocation?.state.Client ||
        returnLabMvdLocation?.state.Client ||
        client,
      Domain:
        promotedLabDomainLocation?.state.Domain ||
        returnLabMvdLocation?.state.Domain ||
        selectedDomain,
      Standard_Code_System:
        promotedLabDomainLocation?.state.Standard_Code_System ||
        returnLabMvdLocation?.state.Standard_Code_System ||
        selectedSystem,
    },
  });
  const [cloneTo, setCloneTo] = useState({});
  const [isOpenAddEditDialog, setIsOpenAddEditDialog] = useState(false);
  const [isOpenDeleteRowDialog, setIsOpenDeleteRowDialog] = useState(false);
  const [isOpenBulkUploadDialog, setIsOpenBulkUploadDialog] = useState(false);
  const [isOpenBulkUploadPreviewDialog, setIsOpenBulkUploadPreviewDialog] =
    useState(false);
  const [isOpenBulkUploadResultsDialog, setIsOpenBulkUploadResultsDialog] =
    useState(false);
  const [isOpenPendingBulkEditDialog, setIsOpenPendingBulkEditDialog] =
    useState(false);
  const [isOpenPublishedActionDialog, setIsOpenPublishedActionDialog] =
    useState(false);
  const [publishedAction, setPublishedAction] = useState(null);
  const [isOpenLabEditDialog, setIsOpenLabEditDialog] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formCallback, setFormCallback] = useState(null);
  const [deleteCallback, setDeleteCallback] = useState(null);
  const [addRowIdIncrementer, setAddRowIdIncrementer] = useState(1);
  const [searchValues, setSearchValues] = useState(
    () => initialLabMvdReturnStateRef.current?.searchValues || {}
  );
  const [mapFetching, setMapFetching] = useState(false);
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [editRowsButtonDisabled, setEditRowsButtonDisabled] = useState(false);
  const [applySavedGridOptions, setApplySavedGridOptions] = useState(false);
  const [focusedRows, setFocusedRows] = useState([]);
  const [currentFocus, setCurrentFocus] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsMetadata, setSelectedRowsMetadata] = useState(
    DEFAULT_SELECTION_METADATA
  );
  const [bulkDeleteButtonDisabled, setBulkDeleteButtonDisabled] =
    useState(false);
  const [isAllStandardCodeSystem, setIsAllStandardCodeSystem] = useState(false);
  const [labTableState, setLabTableState] = useState(
    () => initialLabMvdReturnStateRef.current?.tableState || null
  );
  const [pendingLabMvdReturnState, setPendingLabMvdReturnState] = useState(
    initialLabMvdReturnStateRef.current
  );
  const [hasLabInlineEditsInProgress, setHasLabInlineEditsInProgress] =
    useState(false);
  const [isOpenLabReviewUnsavedDialog, setIsOpenLabReviewUnsavedDialog] =
    useState(false);
  const [pendingLabVersionChange, setPendingLabVersionChange] = useState(null);
  const [versionSelectResetKey, setVersionSelectResetKey] = useState(0);
  const [submissionLoadingTitle, setSubmissionLoadingTitle] = useState(
    DEFAULT_SUBMISSION_LOADING_TITLE
  );
  const [isLabMap, setIsLabMap] = useState(false);
  const [selectedLabMap, setSelectedLabMap] = useState(null);
  const dispatch = useDispatch();
  const [updateMapApi] = useUpdateMapMutation();
  const [updateMapTags] = useUpdateMapTagsMutation();
  const [bulkOperation] = useBulkOperationMutation();
  const { data: labClientsData = [], refetch: refetchLabClients } =
    useGetLabDomainClientsQuery();
  const [triggerGetLabDomainCodeMapping] =
    useLazyGetLabDomainCodeMappingQuery();
  const [updateLabDomainCodeMapping] = useUpdateLabDomainCodeMappingMutation();
  // Destructure the value that the hook returns
  const {
    state: {
      mapValueTags,
      mapStatus,
      mapVersion,
      mapVersionList,
      isEditableVersion,
      allTags,
      mapTags,
      rowData,
      mapColumns,
      mapAttributes,
      tableLoading,
      isSearchDisabled,
      isSearchBtnDisabled,
      setIsSearchBtnDisabled,
      mapID,
      conceptSetData,
    },
    fetchAllTags,
    fetchMapById,
    fetchMapsByIds,
  } = useEIPMapValueDefinition();
  const [triggerGetAttributes] = useLazyGetAttributesQuery();
  const { fetchValidation } = useClone();
  useGetAttrConfigQuery();
  useGetAttrConfigQuery("clone");
  useSearchConceptSetsByConditionQuery(
    SELECT_SEARCH_CONCEPT_SETS_BY_CONDITION_LAB_DOMAIN_CONDITION
  );
  const loincValuesResult = useSelector((state) =>
    selectConceptSetsLoincValues(
      state,
      SELECT_SEARCH_CONCEPT_SETS_BY_CONDITION_LAB_DOMAIN_CONDITION
    )
  );
  const deferredLoincValuesResult = useDeferredValue(loincValuesResult);
  const labLoincValues = deferredLoincValuesResult?.data ?? [];

  const {
    state: {
      submissionFinished,
      setSubmissionFinished,
      submissionErrors,
      setSubmissionErrors,
      submissionHeader,
      setSubmissionHeader,
      spawnSubmissionPopup,
      setSpawnSubmissionPopup,
      submissionSubText,
      setSubmissionSubText,
      submissionCallback,
      setSubmissionCallback,
      saveButtonDisabled,
      setSaveButtonDisabled,
      publishButtonDisabled,
      setPublishButtonDisabled,
      submitForReviewDisabled,
      setSubmitForReviewDisabled,
      addButtonDisabled,
      bulkUploadButtonDisabled,
      setBulkUploadButtonDisabled,
    },
    handleErrorResponse,
    resetButtonOnMapStatus,
  } = useEIPSubmissionOperations();
  const {
    attrConfig,
    attrFilters,
    selectedMapId,
    updatedRecords,
    cloneRecords,
  } = useSelector((state) => state.map || {});
  const pendingRecordIds = new Set(
    (Array.isArray(updatedRecords) ? updatedRecords : [])
      .map((record) => getRecordRowId(record))
      .filter((recordId) => recordId !== undefined)
  );
  const hasPendingRecordChanges = pendingRecordIds.size > 0;

  const shouldBlockPendingBulkOperation = (selectedData) => {
    if (!hasPendingRecordChanges || !Array.isArray(selectedData)) {
      return false;
    }

    return selectedData.length > 1;
  };

  const blockPendingBulkOperation = (selectedData) => {
    if (!shouldBlockPendingBulkOperation(selectedData)) {
      return false;
    }

    setIsOpenPendingBulkEditDialog(true);
    return true;
  };

  const [searchMapsApi] = useSearchMapsMutation();

  const labMapByUuid = useMemo(
    () =>
      labClientsData.reduce((acc, labMap) => {
        if (labMap?.uuid) {
          acc[labMap.uuid] = labMap;
        }
        return acc;
      }, {}),
    [labClientsData]
  );

  const editRowsButtonDisabledHandler = (disabled) => {
    setEditRowsButtonDisabled(disabled);
  };

  const bulkDeleteButtonDisabledHandler = (disabled) => {
    setBulkDeleteButtonDisabled(disabled);
  };

  const handleSelectedRowsChange = (rows, metadata = {}) => {
    setSelectedRows(rows);
    setSelectedRowsMetadata({
      ...DEFAULT_SELECTION_METADATA,
      ...metadata,
    });
  };

  const getCurrentSearchValues = () => {
    if (Object.keys(searchValues).length) {
      return searchValues;
    }

    return {
      Client: mapAttributes.Client?.value,
      Domain: mapAttributes.Domain?.value,
      Standard_Code_System: mapAttributes.Standard_Code_System?.value,
    };
  };

  const getLabMvdReturnState = () => ({
    searchValues: getCurrentSearchValues(),
    selectedVersion: mapVersion || "MAX",
    tableState: labTableState,
  });

  // componentdidmount
  useEffect(() => {
    let mounted = true;
    async function fetchTags() {
      if (mounted) {
        await fetchAllTags();
      }
    }
    fetchTags();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    async function updateGrid() {
      if (cloneRecords.length > 0) {
        dispatch(setCloneRecords([]));
        setSaveButtonDisabled(false);
      }
      //disable Review/Publish button if there is no data in new grid load
      if (rowData && rowData.length === 0) {
        setSubmitForReviewDisabled(true);
        setPublishButtonDisabled(true);
      }
    }
    updateGrid();
  }, [cloneRecords, dispatch, rowData]);

  useEffect(() => {
    async function handleSearch() {
      if (
        newMapLocation &&
        newMapLocation.state &&
        Object.keys(searchValues).length > 2
      ) {
        await handleMapSearch(
          pendingLabMvdReturnState?.selectedVersion || "MAX"
        );
        if (pendingLabMvdReturnState) {
          clearLabMvdReturnState();
          setPendingLabMvdReturnState(null);
        }
        dispatch(resetNewMapLocation());
        setNewMapLocation({ ...newMapLocation, state: null });
      }
    }
    handleSearch();
  }, [searchValues]);

  // componentdidupdate (state)
  useEffect(() => {
    if (updatedRecords.length) {
      setSaveButtonDisabled(false); // Enable the save button
      setSubmitForReviewDisabled(true); // Disable review Button
    } else {
      const updatedAttr = getUpdatedAttr(mapAttributes);
      if (!updatedAttr.length) {
        setSaveButtonDisabled(true); // Disable save button
        setSubmitForReviewDisabled(false); // Enable review button
      }
    }
  }, [updatedRecords]);

  const bulkUpload = () => {
    setBulkUploadButtonDisabled(false);

    const bulkUploadFormData = {};
    bulkUploadFormData.mode = "Bulk Upload";

    setFormCallback(() => (response) => handleBulkUploadFormData(response));

    setFormData(bulkUploadFormData);
    setIsOpenBulkUploadDialog(true);
  };

  const bulkDelete = () => {
    setBulkDeleteButtonDisabled(false);

    // if from event, then do single, else bulk
    const selectedData = selectedRows;
    if (selectedData.length === 0) {
      return;
    }

    if (blockPendingBulkOperation(selectedData)) {
      return;
    }

    selectedData.forEach((element) => {
      if (element.flags && element.flags.deleted === true) {
        return;
      }
    });

    const bulkDeleteFormData = {};
    bulkDeleteFormData.records = selectedData;

    // Set the form to use "delete" callback
    setDeleteCallback(() => (response) => handleMultiDeleteFormData(response));

    setFormData(bulkDeleteFormData);
    setIsOpenDeleteRowDialog(true);
  };

  const bulkUploadPreview = async (bulkFile) => {
    const validatedReturn = await fetchValidatedRows(bulkFile, "validate");
    if (validatedReturn.rows) {
      setIsOpenBulkUploadDialog(false);
      const bulkUploadPreviewFormData = getBulkUploadFormData(
        validatedReturn.rows,
        bulkFile
      );
      setFormData(bulkUploadPreviewFormData);
      setFormCallback(
        () => (response) => handleBulkUploadPreviewFormData(response)
      );
      setIsOpenBulkUploadPreviewDialog(true);
      return true;
    } else {
      alert(validatedReturn.errors + "\nPlease review file and resubmit.");
      return false;
    }
  };

  const bulkUploadResults = async (bulkFile) => {
    const uploadedReturn = await fetchValidatedRows(bulkFile, "upload");
    setIsOpenBulkUploadPreviewDialog(false);
    const bulkUploadResultsFormData = getBulkUploadFormData(
      uploadedReturn.rows,
      bulkFile
    );
    setFormData(bulkUploadResultsFormData);
    setFormCallback(() => () => handleBulkUploadResultsFormData());

    setIsOpenBulkUploadResultsDialog(true);
  };

  const addRow = () => {
    // Ensure row is fully populated
    const rowData = {};
    rowData.mode = "Add";
    rowData.localCode = "";
    rowData.localDescription = "";
    rowData.standardDescription = "";
    rowData.standardCode = "";
    rowData.standardCodeSystem = "";
    rowData.localSourceSystem = "";
    rowData.standardCodeDropdownItems = getStandardCodeDropdownValues(
      rowData.mode
    );
    rowData.mapValueTags = mapValueTags;
    rowData.tags = [];
    rowData.dataSourceValue = "";
    rowData.synSourceValue = "";
    setFormData(rowData);
    setFormCallback(() => (response) => handleAddFormData(response));
    setIsOpenAddEditDialog(true);
  };

  const editRows = () => {
    const selectedData = selectedRows;
    if (selectedData.length === 0) {
      return;
    }

    if (isLabMap) {
      setIsOpenLabEditDialog(true);
      return;
    }

    if (blockPendingBulkOperation(selectedData)) {
      return;
    }

    // TODO: remove the delete flag check when no longer possible for deleted rows to be edited
    selectedData.forEach((element) => {
      if (
        element.flags &&
        (element.flags.deleted === true || element.flags.add === true)
      ) {
        return;
      }
    });

    const bulkEditFormData = {};
    bulkEditFormData.rows = selectedData;
    bulkEditFormData.bulkEditSelection = {
      ...selectedRowsMetadata,
      mapId: mapID,
    };

    if (selectedData.length === 1) {
      bulkEditFormData.mode = "Edit";
      const editingRowData = selectedData[0];
      bulkEditFormData.localCode = checkRow(editingRowData.localCode);
      bulkEditFormData.standardDescription = checkRow(
        editingRowData.standardDescription
      );
      bulkEditFormData.localDescription = checkRow(
        editingRowData.localDescription
      );
      bulkEditFormData.standardCode = checkRow(editingRowData.standardCode);
      bulkEditFormData.standardCodeSystem = checkRow(
        editingRowData.standardCodeSystem
      );
      bulkEditFormData.localSourceSystem = checkRow(
        editingRowData.localSourceSystem
      );
      bulkEditFormData.status = checkRow(editingRowData.status);
      bulkEditFormData.comments = checkRow(editingRowData.comments);
      //todo: these data/syn source values are not referencing the right data structure in editingRowingData, resulting in empty presets
      bulkEditFormData.dataSourceValue = checkRow(
        editingRowData.dataSourceValue
      );
      bulkEditFormData.synSourceValue = checkRow(editingRowData.synSourceValue);
    } else {
      bulkEditFormData.mode = "Bulk Edit";
      bulkEditFormData.localCode = "UNCHANGED";
      bulkEditFormData.localDescription = "UNCHANGED";
      bulkEditFormData.standardDescription = "UNCHANGED";
      bulkEditFormData.standardCode = "UNCHANGED";
      bulkEditFormData.localSourceSystem = "UNCHANGED";
      bulkEditFormData.standardCodeSystem = "UNCHANGED";
      bulkEditFormData.comments = "UNCHANGED";
      bulkEditFormData.status = "UNCHANGED";
      bulkEditFormData.dataSourceValue = "UNCHANGED";
      bulkEditFormData.synSourceValue = "UNCHANGED";
    }
    bulkEditFormData.mapValueTags = mapValueTags;
    bulkEditFormData.tags = [];
    bulkEditFormData.standardCodeDropdownItems = getStandardCodeDropdownValues(
      bulkEditFormData.mode
    );

    setFormData(bulkEditFormData);
    // Set the form to use "edit" callback
    setFormCallback(() => (response) => handleMultiEditFormData(response));
    setIsOpenAddEditDialog(true);
  };

  const getStandardCodeDropdownValues = (mode) => {
    return buildStandardCodeDropdownValues(conceptSetData.values, mode);
  };

  const getStandardCodeDetails = (code) => {
    const concept = conceptSetData.values?.find(
      (concept) => (concept.code ?? "") === code
    );

    if (!concept) {
      return undefined;
    }

    return {
      description: concept?.description ?? "",
      longDescription: concept?.longDescription,
    };
  };

  const cloneRows = () => {
    // retrieve all rows to clone
    const selectedData = selectedRows;
    if (selectedData.length === 0) {
      return;
    }

    if (blockPendingBulkOperation(selectedData)) {
      return;
    }

    // TODO: remove the delete flag check when no longer possible for deleted rows to be cloned
    selectedData.forEach((element) => {
      if (element.flags && element.flags.deleted === true) {
        return;
      }
    });

    const bulkCloneFormData = {
      rows: selectedData,
      mode: "Clone",
      localCode: UNCHANGED,
      localDescription: UNCHANGED,
      standardDescription: UNCHANGED,
      standardCode: UNCHANGED,
      localSourceSystem: UNCHANGED,
      standardCodeSystem: UNCHANGED,
      comments: UNCHANGED,
      status: UNCHANGED,
      dataSourceValue: UNCHANGED,
      synSourceValue: UNCHANGED,
      mapValueTags,
      tags: [],
      standardCodeDropdownItems: getStandardCodeDropdownValues("Clone"),
    };
    // Memoize the callback to avoid new function reference on every render
    const memoizedFormCallback = (response) =>
      handleMultiCloneFormData(response);

    setFormCallback(() => memoizedFormCallback);
    setFormData(bulkCloneFormData);

    setIsOpenAddEditDialog(true);
  };

  const closeAddEditDialog = () => {
    setIsOpenAddEditDialog(false);
    setFormData();
  };

  const closeLabEditDialog = () => {
    setIsOpenLabEditDialog(false);
  };

  const closeBulkUploadDialog = () => {
    setIsOpenBulkUploadDialog(false);
  };

  const closeBulkUploadPreviewDialog = () => {
    setIsOpenBulkUploadPreviewDialog(false);
  };

  const closeBulkUploadResultsDialog = () => {
    setIsOpenBulkUploadResultsDialog(false);
    //if user made it this far, we want to refresh the grid when they close the dialog to load the newest data
    handleMapSearch("MAX");
  };

  const closePendingBulkEditDialog = () => {
    setIsOpenPendingBulkEditDialog(false);
  };

  const closeLabReviewUnsavedDialog = () => {
    setIsOpenLabReviewUnsavedDialog(false);
    setPendingLabVersionChange(null);
  };

  const confirmLabReviewUnsavedDialog = async () => {
    const versionToLoad = pendingLabVersionChange;
    setIsOpenLabReviewUnsavedDialog(false);
    setPendingLabVersionChange(null);

    if (versionToLoad !== null) {
      await handleMapSearch(versionToLoad);
      return;
    }

    await handleUpdateMapStatus("REVIEW");
  };

  const savePendingBulkEditChanges = async () => {
    setIsOpenPendingBulkEditDialog(false);
    await saveAsPublicDraft();
  };

  const closeDeleteDialog = () => {
    setIsOpenDeleteRowDialog(false);
  };

  const handleBulkUploadFormData = async (response) => {
    return await bulkUploadPreview(response);
  };

  const handleBulkUploadPreviewFormData = (response) => {
    bulkUploadResults(response);
  };

  const handleBulkUploadResultsFormData = () => {
    setIsOpenBulkUploadResultsDialog(false);
  };

  const handleAddFormData = async (rows) => {
    setIsOpenAddEditDialog(false);

    let row = {};
    // For new row _id field would be null. We would use addRowId to differentiate between newly added rows.
    // we are not making use of _id as this should be set by backend api which is an objectID
    rows.forEach((response) => {
      row = {
        ...response,
        _id: null,
        isNew: true,
        addRowId: addRowIdIncrementer,
      };
    });

    setAddRowIdIncrementer(addRowIdIncrementer + 1);
    // Pass new row explicitly since updatedRecords closure is stale after dispatch
    const saved = await saveAsPublicDraft(
      {
        records: transformMVRecordsForApiRequest([...updatedRecords, row]),
      },
      { skipSuccessPopup: true }
    );
    // Only add locally if save failed so the user can retry
    if (!saved) {
      dispatch(setAddFormData(row));
    }
  };

  const handleMultiEditFormData = async (rows) => {
    if (rows.bulkEdit) {
      setIsOpenAddEditDialog(false);
      return saveAsPublicDraft(
        { bulkEdit: rows.bulkEdit },
        { skipSuccessPopup: true }
      );
    }

    if (Array.isArray(rows) && rows.length > 1) {
      setIsOpenAddEditDialog(false);
      return saveAsPublicDraft(
        {
          records: transformMVRecordsForApiRequest(rows),
        },
        { skipSuccessPopup: true }
      );
    }

    if (Array.isArray(rows) && rows.length === 1) {
      setIsOpenAddEditDialog(false);
      const saved = await saveAsPublicDraft(
        {
          records: transformMVRecordsForApiRequest(rows),
        },
        { skipSuccessPopup: true }
      );
      if (!saved) {
        dispatch(setMultiEditFormData(rows));
      }
      return saved;
    }

    setIsOpenAddEditDialog(false);
    dispatch(setMultiEditFormData(rows));
    return true;
  };

  const normalizeCloneAttributes = (attributes) => {
    if (Array.isArray(attributes)) {
      return attributes.reduce((acc, item) => {
        acc[item.name] = item.values?.[0];
        return acc;
      }, {});
    }

    return attributes || {};
  };

  const validateCloneRows = async ({ attributes, cloneRows }) => {
    const searchArr = Object.keys(attributes).map((key) => ({
      name: key,
      values: [attributes[key]],
    }));

    return fetchValidation(searchArr, cloneRows);
  };

  const handleMultiCloneFormData = async (response) => {
    setIsOpenAddEditDialog(false);
    setFormData();

    const cloneAttributes = normalizeCloneAttributes(response.attributes);
    const cloneSearchValues = response.searchValues || cloneAttributes;

    setSearchValues(cloneSearchValues);
    setSpawnSubmissionPopup(true);
    setSubmissionLoadingTitle("Saving...");
    const targetMapId = await handleMapSearch("MAX", cloneSearchValues);
    if (!targetMapId) {
      setSpawnSubmissionPopup(false);
      setSubmissionLoadingTitle(DEFAULT_SUBMISSION_LOADING_TITLE);
      return false;
    }
    const cloneTo = {
      state: {
        Client: cloneAttributes.Client,
        Domain: cloneAttributes.Domain,
        Standard_Code_System: cloneAttributes.Standard_Code_System,
      },
    };

    dispatch(setAttrFilters(response.attributeLists));

    setCloneTo(cloneTo);
    return saveAsPublicDraft(
      {
        records: transformMVRecordsForApiRequest(response.cloneRows),
      },
      {
        skipSuccessPopup: true,
        targetMapId,
      }
    );
  };

  const handleMultiDeleteFormData = async (response) => {
    setIsOpenDeleteRowDialog(false);

    const recordsToDelete = response.records
      .filter((record) => record._id)
      .map((record) => ({ ...record, delete: true }));

    if (!recordsToDelete.length) {
      dispatch(setMultiDeleteFormData(response));
      return true;
    }

    const saved = await saveAsPublicDraft(
      {
        records: transformMVRecordsForApiRequest(recordsToDelete),
      },
      { skipSuccessPopup: true, loadingTitle: "Deleting..." }
    );

    if (!saved) {
      dispatch(setMultiDeleteFormData(response));
    }

    return saved;
  };

  const handleFocusChange = (filters) => {
    const filterKeys = Object.keys(filters);

    if (filterKeys.length) {
      const oldRowData = [...rowData];

      const filteredRows = oldRowData.filter((row) => {
        return filterKeys.every((key) =>
          rowMatchesTagFilter(row, key, filters)
        );
      });
      setFocusedRows(filteredRows);
    } else {
      setFocusedRows([]);
    }
  };

  const applyTags = async (tags) => {
    try {
      setSpawnSubmissionPopup(true);

      const payload = {
        tags,
      };
      await updateMapTags({
        selectedMapId: mapID,
        payload,
      });
      handleApiResponse(null, mapVersion);
    } catch (error) {
      handleApiResponse(error, mapVersion);
    }
  };

  /*
  const cloneSelectedRows = async () => {
    setSpawnSubmissionPopup(true);
    const updatedAttr = getUpdatedAttr(mapAttributes);

    const requestBody = {
      attributes: updatedAttr,
      mapElements: {
        records: transformMVRecordsForApiRequest(updatedRecords),
      },
    };

    const response = await fetch(`${BASE_URL}/maps/${selectedMapId}`, {
      method: "PATCH",
      headers: headerService.getAllHeaders(),
      body: JSON.stringify(requestBody),
    });

    const json = await response.json();
    console.log(json);
    await fetchNewVersionList();
    handleApiResponse(response);
  };
 */

  const saveAsPublicDraft = async (mapElementsOverride, options = {}) => {
    setApplySavedGridOptions(true);
    setSpawnSubmissionPopup(true);
    setSubmissionLoadingTitle(options.loadingTitle || "Saving...");

    const updatedAttr = getUpdatedAttr(mapAttributes);
    const hasMapElementsOverride =
      mapElementsOverride &&
      typeof mapElementsOverride === "object" &&
      (Object.hasOwn(mapElementsOverride, "records") ||
        Object.hasOwn(mapElementsOverride, "bulkEdit"));

    const payload = {
      attributes: updatedAttr,
      mapElements: hasMapElementsOverride
        ? mapElementsOverride
        : {
            records: transformMVRecordsForApiRequest(updatedRecords),
          },
    };

    try {
      await updateMapApi({
        selectedMapId: options.targetMapId || selectedMapId,
        payload,
      }).unwrap();
      const versionListRefresh = fetchNewVersionList();
      await handleApiResponse(undefined, undefined, options);
      await versionListRefresh;
      return true;
    } catch (error) {
      await handleApiResponse(error, undefined, options);
      return false;
    }
  };

  const handleUpdateMapStatus = async (status) => {
    try {
      setSpawnSubmissionPopup(true);
      setApplySavedGridOptions(false);
      await updateMapApi({ selectedMapId, payload: { status } }).unwrap();
      dispatch(setMapStatus(status));
      await handleApiResponse();
      if (isLabMap) {
        await handleMapSearch("MAX", undefined, { refreshLabClients: true });
      }
    } catch (error) {
      handleApiResponse(error);
    }
  };

  const handleMapStatusAction = (status) => {
    if (status === "REVIEW" && isLabMap && hasLabInlineEditsInProgress) {
      setIsOpenLabReviewUnsavedDialog(true);
      return Promise.resolve(false);
    }

    return handleUpdateMapStatus(status);
  };

  const handleVersionChangeAction = (selectedVersion) => {
    if (isLabMap && hasLabInlineEditsInProgress) {
      setPendingLabVersionChange(selectedVersion);
      setVersionSelectResetKey((key) => key + 1);
      setIsOpenLabReviewUnsavedDialog(true);
      return Promise.resolve(false);
    }

    return handleMapSearch(selectedVersion);
  };

  const handleLabEditConfirm = async (payload) => {
    try {
      setIsOpenLabEditDialog(false);
      setSpawnSubmissionPopup(true);
      setSubmissionLoadingTitle("Saving...");
      const result = await updateLabDomainCodeMapping(payload).unwrap();
      setSubmissionErrors([]);
      dispatch(setUpdatedRecords([]));

      if (
        result?.mapUuid &&
        (result.mapUuid !== selectedMapId ||
          result.mapVersion !== selectedLabMap?.version)
      ) {
        await handleMapSearch("MAX", undefined, { refreshLabClients: true });
      } else if (selectedLabMap?.uuid) {
        await fetchNewVersionList();
        await loadLabMapRows(selectedLabMap, mapVersion || "MAX");
      }

      setSpawnSubmissionPopup(false);
      setSubmissionFinished(false);
      setSubmissionLoadingTitle(DEFAULT_SUBMISSION_LOADING_TITLE);
      return true;
    } catch (error) {
      await handleApiResponse(error);
      return false;
    }
  };

  const runWithPublishedConfirmation = (action) => {
    if (mapStatus === "PUBLISHED") {
      setPublishedAction(() => action);
      setIsOpenPublishedActionDialog(true);
      return;
    }

    action();
  };

  const closePublishedActionDialog = () => {
    setIsOpenPublishedActionDialog(false);
    setPublishedAction(null);
  };

  const confirmPublishedActionDialog = () => {
    const action = publishedAction;
    closePublishedActionDialog();
    if (action) {
      action();
    }
  };

  const handleBack = () => {
    console.log("Button Pressed");
  };

  const handleFilterChange = (event, attrObj) => {
    const newSearchValues = { ...searchValues };
    newSearchValues[attrObj.name] = event.value;

    // Reset child filters
    dispatch(resetFilters({ attrObj, searchValues, newSearchValues }));
    setSearchValues(newSearchValues);

    // Get next filter
    if (attrObj.child) {
      triggerGetAttributes({
        configId: attrConfig._id,
        attrName: attrObj.child,
        filters: newSearchValues,
      });
    }

    checkSearchBtn(newSearchValues, setIsSearchBtnDisabled, attrObj.child);
  };

  const resolveLabMapByUuid = async (uuid) => {
    if (!uuid) {
      return null;
    }

    const currentLabMap = labMapByUuid[uuid];
    if (currentLabMap) {
      return currentLabMap;
    }

    try {
      const refreshedLabClients = await refetchLabClients().unwrap();
      return (refreshedLabClients || []).find(
        (labMap) => labMap?.uuid === uuid
      );
    } catch {
      return null;
    }
  };

  const resolveLabMapBySearchValues = async (values, options = {}) => {
    const currentLabMap = findLabClientMap(labClientsData, values);
    if (currentLabMap && !options.refreshLabClients) {
      return currentLabMap;
    }

    try {
      const refreshedLabClients = await refetchLabClients().unwrap();
      return findLabClientMap(refreshedLabClients || [], values);
    } catch {
      return null;
    }
  };

  const isLabDomainSearch = (values) =>
    values?.Domain === LAB_DOMAIN_VALUE ||
    (Array.isArray(values?.Domain) && values.Domain.includes(LAB_DOMAIN_VALUE));

  const loadLabMapRows = async (labMap, selectedVersion = "MAX") => {
    const labRows = await triggerGetLabDomainCodeMapping({
      mapReference: labMap.uuid,
      version: selectedVersion,
    }).unwrap();
    dispatch(setRowData(labRows));
  };

  const handleSearchSuccessResponse = async (
    response,
    selectedVersion,
    activeSearchValues,
    labMapOverride
  ) => {
    const shouldLoadLabData = isLabDomainSearch(activeSearchValues);
    const labMap = labMapOverride
      ? labMapOverride
      : shouldLoadLabData
      ? await resolveLabMapByUuid(response.records[0].uuid)
      : null;
    const nextMapUuid = shouldLoadLabData
      ? labMap?.uuid
      : response.records[0].uuid;
    setIsLabMap(shouldLoadLabData);
    setSelectedLabMap(labMap);
    dispatch(setSelectedMapId(nextMapUuid));
    setSearchValues(activeSearchValues);

    await fetchMapById(
      `${nextMapUuid}?version=${selectedVersion}`,
      resetButtonOnMapStatus
    );
    if (shouldLoadLabData) {
      await loadLabMapRows(labMap, selectedVersion);
    }
    dispatch(setIsSearchDisabled(false));
  };

  // function to fetch new version list
  // version list otherwise set by search function- but drafting/publishing doesn't trigger search yet changes version list
  const fetchNewVersionList = async () => {
    try {
      if (isLabMap) {
        const refreshedLabClients = await refetchLabClients().unwrap();
        const labMap = findLabClientMap(
          refreshedLabClients || labClientsData,
          searchValues
        );

        if (!labMap?.uuid) {
          throw getMissingLabClientError(searchValues);
        }

        setSelectedLabMap(labMap);
        dispatch(setSelectedMapId(labMap.uuid));
        dispatch(setMapVersionList(buildLabMapVersionList(labMap.version)));
        return;
      }

      const searchArr = Object.keys(searchValues).map((key) => ({
        name: key,
        values: [searchValues[key]],
      }));

      const requestBody = { attributes: searchArr };
      const response = await searchMapsApi(requestBody).unwrap();
      dispatch(setMapVersionList(response?.records?.[0]?.versions || []));
    } catch (error) {
      handleErrorResponse(error, setSubmissionErrors); // need better way to reduce code complexity
    }
  };

  const handleMapSearch = async (
    selectedVersion,
    cloneOptions,
    options = {}
  ) => {
    let searchArr;
    let requestBody;
    let searchIsAllStandardCodeSystem = false;
    let targetMapId;
    const activeSearchValues =
      cloneOptions && Object.keys(cloneOptions).length !== 0
        ? cloneOptions
        : searchValues;
    //this option is only necessary because setSearchValues is async in handleMultiCloneFormData
    //and assignment hasn't completed by the time this is called.
    //todo: clean this up to only the else statement once redux is in place
    if (cloneOptions && Object.keys(cloneOptions).length !== 0) {
      searchIsAllStandardCodeSystem =
        activeSearchValues.Standard_Code_System === "ALL";
      searchArr = Object.keys(activeSearchValues).map((key) => ({
        name: key,
        values: [activeSearchValues[key]],
      }));
    } else {
      searchIsAllStandardCodeSystem =
        activeSearchValues.Standard_Code_System === "ALL";
      searchArr = Object.keys(activeSearchValues).reduce((acc, key) => {
        if (key === "Standard_Code_System" && searchIsAllStandardCodeSystem) {
          return acc;
        }

        acc.push({ name: key, values: [activeSearchValues[key]] });
        return acc;
      }, []);
    }

    //reset trackers
    setFocusedRows([]);
    setCurrentFocus({});
    setSelectedRows([]);
    setSelectedRowsMetadata(DEFAULT_SELECTION_METADATA);
    setIsLabMap(false);
    setSelectedLabMap(null);
    if (!pendingLabMvdReturnState) {
      setLabTableState(null);
    }

    setApplySavedGridOptions(false);
    dispatch(setIsSearchDisabled(true));
    setIsSearchBtnDisabled(true);
    dispatch(setTableLoading(true));
    setMapSearchLoading(true);
    setMapFetching(true);
    requestBody = requestBody || { attributes: searchArr };
    setIsAllStandardCodeSystem(searchIsAllStandardCodeSystem);

    //fetch map data using any UUID(s) depending on whether the user selected one or ALL standard code systems
    try {
      let response;
      if (isLabDomainSearch(activeSearchValues)) {
        const labMap = await resolveLabMapBySearchValues(
          activeSearchValues,
          options
        );
        if (!labMap?.uuid) {
          throw getMissingLabClientError(activeSearchValues);
        }

        dispatch(setMapVersionList(buildLabMapVersionList(labMap.version)));
        await handleSearchSuccessResponse(
          { records: [{ uuid: labMap.uuid }] },
          selectedVersion,
          activeSearchValues,
          labMap
        );
        targetMapId = labMap.uuid;
      } else if (searchIsAllStandardCodeSystem) {
        const standardCodeSystemFilter = attrFilters?.find(
          (filter) => filter.name === "Standard_Code_System"
        );
        const standardCodeSystems = (standardCodeSystemFilter?.values || [])
          .map((item) => item.value)
          .filter((value) => value !== "ALL");

        const searchResponses = await Promise.all(
          standardCodeSystems.map((standardCodeSystem) => {
            const attributes = Object.keys(activeSearchValues).map((key) => ({
              name: key,
              values: [
                key === "Standard_Code_System"
                  ? standardCodeSystem
                  : activeSearchValues[key],
              ],
            }));
            return searchMapsApi({ attributes }).unwrap();
          })
        );

        const mapIds = searchResponses
          .map((searchResponse) => searchResponse?.records?.[0]?.uuid)
          .filter(Boolean);

        if (mapIds.length) {
          targetMapId = mapIds[0];
          dispatch(setSelectedMapId(mapIds[0]));
          await fetchMapsByIds(mapIds, resetButtonOnMapStatus);
        } else {
          dispatch(setTableLoading(false));
          dispatch(setIsSearchDisabled(false));
        }
      } else if (searchArr.length > 2) {
        response = await searchMapsApi(requestBody).unwrap();
      }
      dispatch(resetFormData());
      if (response) {
        await handleSearchSuccessResponse(
          response,
          selectedVersion,
          activeSearchValues
        );
        targetMapId = response.records[0].uuid;
      }
    } catch (err) {
      setSpawnSubmissionPopup(true);
      setSubmissionFinished(true);
      setSubmissionHeader("An error has occurred.");
      setSubmissionSubText("Something went wrong while loading your map.");
      submissionFailed();
      await handleErrorResponse(err, setSubmissionErrors);
      dispatch(setTableLoading(false));
      dispatch(setIsSearchDisabled(false));
      setMapFetching(false);
    } finally {
      setMapSearchLoading(false);
    }

    setIsSearchBtnDisabled(false);
    return targetMapId;
  };

  const handleApiResponse = async (apiError, selectedVersion, options = {}) => {
    if (apiError) {
      // State changes for all API errors
      setSubmissionFinished(true);
      setSubmissionHeader("An error has occurred.");
      setSubmissionSubText(
        "Something went wrong while submitting your request."
      );
      submissionFailed();
      handleErrorResponse(apiError, setSubmissionErrors); // need better way to reduce code complexity
    } else {
      setSubmissionErrors([]);
      dispatch(setUpdatedRecords([]));
      dispatch(resetFormData());

      if (options.skipSuccessPopup) {
        setSubmissionFinished(false);
        const mapIdToRefresh = options.targetMapId || selectedMapId;
        if (selectedVersion) {
          await fetchMapById(
            mapIdToRefresh + `?version=${selectedVersion}`,
            resetButtonOnMapStatus
          );
        } else {
          await fetchMapById(mapIdToRefresh, resetButtonOnMapStatus);
        }
        setSpawnSubmissionPopup(false);
        setSubmissionLoadingTitle(DEFAULT_SUBMISSION_LOADING_TITLE);
        return;
      }

      setSubmissionFinished(true);
      setSubmissionHeader("Your map has been saved successfully!");
      setSubmissionSubText("The table will refresh with the updated changes.");
      setSubmissionCallback(() => async () => {
        setSpawnSubmissionPopup(false);
        setSubmissionFinished(false);
        setSubmissionLoadingTitle(DEFAULT_SUBMISSION_LOADING_TITLE);
        if (selectedVersion) {
          await fetchMapById(
            selectedMapId + `?version=${selectedVersion}`,
            resetButtonOnMapStatus
          );
        } else {
          await fetchMapById(selectedMapId, resetButtonOnMapStatus);
        }
      });
    }
  };

  //function to fetch validated rows of a bulk upload file
  //callType expects a string of either 'validate' or 'upload'
  const fetchValidatedRows = async (bulkFile, callType) => {
    try {
      const payload = new FormData();
      let rows;
      const resolvedFile = resolveBulkUploadFile(bulkFile);
      payload.append("client", mapAttributes["Client"]?.value);
      payload.append("domain", mapAttributes["Domain"]?.value);
      if (!resolvedFile) {
        throw new Error("Selected upload is not a valid file.");
      }
      payload.append("src", resolvedFile, resolvedFile.name);

      const result = await bulkOperation({
        mapID,
        callType,
        payload,
      });
      const data = result?.data;
      const apiError =
        result?.error?.data?.error ||
        result?.error?.error ||
        result?.error?.message;
      if (typeof apiError === "string") {
        return { errors: apiError };
      }
      const responseRecords =
        data && typeof data === "object" && !Array.isArray(data)
          ? data.records
          : data;
      if (
        data &&
        typeof data === "object" &&
        typeof data.error === "string" &&
        (!Array.isArray(responseRecords) || responseRecords.length === 0)
      ) {
        return { errors: data.error };
      }
      rows = handleValidatedRows(data);
      return { rows };
    } catch (error) {
      let errorResponse;
      if (error?.data?.error) {
        errorResponse = error.data.error;
      } else if (Object.hasOwn(error, "error")) {
        errorResponse = error.error;
      } else if (Object.hasOwn(error, "message")) {
        errorResponse = error.message;
      }
      handleErrorResponse(error, setSubmissionErrors, { ...error }); // need better way to reduce code complexity
      return { errors: errorResponse };
    }
  };

  const submissionFailed = () =>
    setSubmissionCallback(() => () => {
      setSubmissionFinished(false);
      setSpawnSubmissionPopup(false);
      setSubmissionLoadingTitle(DEFAULT_SUBMISSION_LOADING_TITLE);
    });

  return (
    <>
      {/* Replace HOC with direct conditional rendering for dialogs */}
      {isOpenBulkUploadDialog && (
        <BulkUploadForm
          isOpen={isOpenBulkUploadDialog}
          onClose={closeBulkUploadDialog}
          formCallback={formCallback}
          formData={formData}
          onComplete={() => {
            setIsOpenBulkUploadDialog(false);
            setIsOpenBulkUploadPreviewDialog(true);
          }}
          bulkUploadPreviewOnClick={bulkUploadPreview}
          activeDomain={mapAttributes["Domain"]}
        />
      )}
      {isOpenBulkUploadPreviewDialog && (
        <BulkUploadPreviewForm
          isOpen={isOpenBulkUploadPreviewDialog}
          onClose={closeBulkUploadPreviewDialog}
          formCallback={formCallback}
          formData={formData}
          onComplete={() => {
            setIsOpenBulkUploadPreviewDialog(false);
            setIsOpenBulkUploadResultsDialog(true);
          }}
          activeDomain={mapAttributes["Domain"]}
        />
      )}
      {isOpenBulkUploadResultsDialog && (
        <BulkUploadResultsForm
          isOpen={isOpenBulkUploadResultsDialog}
          onClose={closeBulkUploadResultsDialog}
          formCallback={formCallback}
          formData={formData}
          activeDomain={mapAttributes["Domain"]}
        />
      )}
      {isOpenAddEditDialog && (
        <EIPAddEditRowForm
          isOpen={isOpenAddEditDialog}
          onClose={closeAddEditDialog}
          records={rowData}
          formCallback={formCallback}
          formData={formData}
          cloneValidation={validateCloneRows}
          getStandardCodeDetails={getStandardCodeDetails}
          sourceMapLocation={{
            client: mapAttributes.Client?.value,
            domain: mapAttributes.Domain?.value,
          }}
        />
      )}
      {isOpenDeleteRowDialog && (
        <DeleteRowForm
          isOpen={isOpenDeleteRowDialog}
          onClose={closeDeleteDialog}
          deleteCallback={deleteCallback}
          formData={formData}
        />
      )}
      {isOpenPendingBulkEditDialog && (
        <ModalDialog
          isOpen={isOpenPendingBulkEditDialog}
          header="Unsaved Changes"
          onClose={closePendingBulkEditDialog}
          footer={
            <AbyssLayout.Group alignLayout="right">
              <Button
                variant="outline"
                color="neutral"
                onClick={closePendingBulkEditDialog}
              >
                Cancel
              </Button>
              <Button onClick={savePendingBulkEditChanges}>
                Save as Draft
              </Button>
            </AbyssLayout.Group>
          }
        >
          You must save your changes before editing multiple records.
        </ModalDialog>
      )}
      {isOpenPublishedActionDialog && (
        <ModalDialog
          isOpen={isOpenPublishedActionDialog}
          header="Create New Draft Version"
          onClose={closePublishedActionDialog}
          footer={
            <AbyssLayout.Group alignLayout="right">
              <Button
                variant="outline"
                color="neutral"
                onClick={closePublishedActionDialog}
              >
                Cancel
              </Button>
              <Button onClick={confirmPublishedActionDialog}>Confirm</Button>
            </AbyssLayout.Group>
          }
        >
          This action will create a new draft version of the published map.
        </ModalDialog>
      )}
      {isOpenLabReviewUnsavedDialog && (
        <ModalDialog
          isOpen={isOpenLabReviewUnsavedDialog}
          header="Unsaved Inline Edits"
          onClose={closeLabReviewUnsavedDialog}
          footer={
            <AbyssLayout.Group alignLayout="right">
              <Button
                variant="outline"
                color="neutral"
                onClick={closeLabReviewUnsavedDialog}
              >
                Cancel
              </Button>
              <Button onClick={confirmLabReviewUnsavedDialog}>Continue</Button>
            </AbyssLayout.Group>
          }
        >
          You have unsaved inline edits. If you{" "}
          {pendingLabVersionChange !== null
            ? "change versions"
            : "submit this map for review"}
          , those edits will be lost.
        </ModalDialog>
      )}
      {spawnSubmissionPopup && (
        <SubmissionPopup
          isOpen={submissionFinished}
          onClose={submissionCallback}
          submissionHeader={submissionHeader}
          submissionSubText={submissionSubText}
          submissionErrors={submissionErrors}
          loadingTitle={submissionLoadingTitle}
          loadingMessage={
            submissionLoadingTitle === "Deleting..."
              ? "Please wait while the selected row(s) are being deleted."
              : "Please wait while your changes are being saved."
          }
          ariaLoadingLabel={submissionLoadingTitle}
        />
      )}
      <Layout>
        <div className="grid">
          <RouteDisplay
            pageRouteDisplay={[
              PATHS.mapManagement,
              PATHS.editMap,
              PATHS.editMapMapValueDefinition,
            ]}
          />
          <MapLookupContainer
            newMapLocation={newMapLocation}
            selectedDomain={searchValues.Domain}
            cloneTo={cloneTo}
            isSearchBtnDisabled={isSearchBtnDisabled}
            handleMapSearch={handleMapSearch}
            handleFilterChange={handleFilterChange}
            isSearchDisabled={isSearchDisabled || mapSearchLoading}
          ></MapLookupContainer>
          <EIPMapValueDefinitionContainer
            mapFetching={mapFetching}
            tableLoading={tableLoading || mapSearchLoading}
            suppressTableLoadingOverlay={
              spawnSubmissionPopup && !submissionFinished
            }
            saveButtonDisabled={saveButtonDisabled}
            publishButtonDisabled={publishButtonDisabled}
            submitForReviewDisabled={submitForReviewDisabled}
            addButtonDisabled={addButtonDisabled}
            editRowsButtonDisabled={editRowsButtonDisabled}
            bulkUploadButtonDisabled={bulkUploadButtonDisabled}
            bulkDeleteButtonDisabled={bulkDeleteButtonDisabled}
            mapStatus={mapStatus}
            mapVersion={mapVersion}
            mapTags={mapTags}
            allTags={allTags}
            mapValueTags={mapValueTags}
            applyTags={applyTags}
            mapAttributes={mapAttributes}
            versionSelectResetKey={versionSelectResetKey}
            rowData={rowData}
            mapColumns={mapColumns}
            editRowsButtonDisabledHandler={editRowsButtonDisabledHandler}
            bulkDeleteButtonDisabledHandler={bulkDeleteButtonDisabledHandler}
            onSelectedRowsChange={handleSelectedRowsChange}
            handleMultiEditFormData={handleMultiEditFormData}
            addRow={() => runWithPublishedConfirmation(addRow)}
            editRows={() => runWithPublishedConfirmation(editRows)}
            cloneRows={cloneRows}
            bulkUpload={() => runWithPublishedConfirmation(bulkUpload)}
            bulkDelete={() => runWithPublishedConfirmation(bulkDelete)}
            bulkUploadPreview={bulkUploadPreview}
            handleBack={handleBack}
            saveAsPublicDraft={() =>
              runWithPublishedConfirmation(saveAsPublicDraft)
            }
            handleUpdateMapStatus={handleMapStatusAction}
            mapVersionList={mapVersionList}
            handleVersionChange={handleVersionChangeAction}
            isEditableVersion={isEditableVersion}
            handleFocusChange={handleFocusChange}
            applySavedGridOptions={applySavedGridOptions}
            currentFocus={currentFocus}
            setCurrentFocus={setCurrentFocus}
            focusedRows={focusedRows}
            isAllStandardCodeSystem={isAllStandardCodeSystem}
            updatedRecords={updatedRecords}
            isLabMap={isLabMap}
            labLoincValues={labLoincValues}
            isOpenLabEditDialog={isOpenLabEditDialog}
            closeLabEditDialog={closeLabEditDialog}
            handleLabEditConfirm={handleLabEditConfirm}
            initialLabTableState={labTableState}
            onLabTableStateChange={setLabTableState}
            onLabInlineEditingChange={setHasLabInlineEditsInProgress}
            getLabMvdReturnState={getLabMvdReturnState}
          ></EIPMapValueDefinitionContainer>{" "}
        </div>
      </Layout>
    </>
  );
};

function getUpdatedAttr(attrs) {
  const updatedAttr = Object.keys(attrs).map((key) => {
    const obj = attrs[key];

    if (obj.changed) {
      return {
        name: obj.name,
        values: [obj.value],
      };
    }

    return null;
  });
  return updatedAttr.filter((x) => x !== null);
}

function checkSearchBtn(searchVals, setIsSearchBtnDisabled, hasChild) {
  if (Object.keys(searchVals).length && !hasChild) {
    setIsSearchBtnDisabled(false);
  } else {
    setIsSearchBtnDisabled(true);
  }
}

function checkRow(row) {
  return !row ? "" : row;
}

export default EIPMapValueDefinition;
